import {
  CARE_LEVELS,
  DRV_TRAVEL_TYPES,
  OUTPATIENT_NOT_POSSIBLE_REASONS,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { INFECTIONS_CHARACTER_LIMIT } from "core/schemas/shared";
import ConnectedCheckboxGroup, {
  CheckboxGroupProps,
} from "ds_legacy/components/CheckboxGroup";
import { SelectOption } from "ds_legacy/components/SelectInput";
import { margin, sizing } from "ds_legacy/materials/metrics";
import {
  Section,
  SelectWithLabel,
  TextAreaWithLabel,
  TextInputWithLabel,
  YesNoRadioWithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { useTranslations } from "translations";

export const SectionFive = () => {
  const translations = useTranslations();
  const getOntology = useGetOntology();

  const patientCapabilityTravelOptions: CheckboxGroupProps["items"] = [
    {
      id: DRV_TRAVEL_TYPES.PUBLIC,
      label: translations.patientForms.drvMedicalForm.sectionFive.travelPublic,
    },
    {
      id: DRV_TRAVEL_TYPES.CAR,
      label: translations.patientForms.drvMedicalForm.sectionFive.travelCar,
    },
  ];

  const outpatientReasonOptions: CheckboxGroupProps["items"] = [
    {
      id: OUTPATIENT_NOT_POSSIBLE_REASONS.ACCOMPANYING_PERSON,
      label:
        translations.patientForms.drvMedicalForm.sectionFive.accompanyingPerson,
    },
    {
      id: OUTPATIENT_NOT_POSSIBLE_REASONS.TAXI,
      label: translations.patientForms.drvMedicalForm.sectionFive.taxi,
    },
    {
      id: OUTPATIENT_NOT_POSSIBLE_REASONS.AMBULANCE,
      label: translations.patientForms.drvMedicalForm.sectionFive.lyingDown,
    },
  ];

  const carelevels = [
    CARE_LEVELS.NONE,
    CARE_LEVELS.ONE,
    CARE_LEVELS.TWO,
    CARE_LEVELS.THREE,
    CARE_LEVELS.FOUR,
    CARE_LEVELS.FIVE,
  ];

  const carelevelSelectOptions: SelectOption[] = carelevels.map(
    (carelevel) => ({
      id: carelevel,
      label: getOntology({ key: carelevel, type: ONTOLOGIES.CARE_LEVEL }),
      value: carelevel,
    }),
  );

  return (
    <Section title={translations.patientForms.drvMedicalForm.sectionFive.title}>
      <YesNoRadioWithLabel
        elementName="eat_without_help"
        label={translations.patientForms.drvMedicalForm.sectionFive.eat}
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="wash_and_dress_without_help"
        label={translations.patientForms.drvMedicalForm.sectionFive.wash}
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="mobility_walk_without_help"
        label={translations.patientForms.drvMedicalForm.sectionFive.move}
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="wound_closed"
        label={translations.patientForms.drvMedicalForm.sectionFive.wound}
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="infections_selected"
        label={translations.patientForms.drvMedicalForm.sectionFive.germs}
        shouldStartWithNo
        sideMutation={(_value, mutate) => {
          mutate(null, "infections");
        }}
        subForms={{
          [PREDICAMENT_STATE_YES]: (
            <TextInputWithLabel
              characterLimit={INFECTIONS_CHARACTER_LIMIT}
              elementName="infections"
              isLarge
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .germsDescription
              }
              marginOverride={margin(0, 0, 1)}
              withFlatModel
            />
          ),
        }}
      />
      <YesNoRadioWithLabel
        elementName="urinary_incontinence"
        label={
          translations.patientForms.drvMedicalForm.sectionFive
            .urinalIncontinence
        }
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="needs_catheter_selected"
        label={translations.patientForms.drvMedicalForm.sectionFive.catheters}
        shouldStartWithNo
        sideMutation={(_value, mutate) => {
          mutate(null, "needs_catheter");
        }}
        subForms={{
          [PREDICAMENT_STATE_YES]: (
            <TextAreaWithLabel
              elementName="needs_catheter"
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .cathetersDescription
              }
              marginOverride={margin(0, 0, 1)}
              withFlatModel
            />
          ),
        }}
      />
      <YesNoRadioWithLabel
        elementName="fecal_incontinence"
        label={
          translations.patientForms.drvMedicalForm.sectionFive.incontinence
        }
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="mobility_wheel_chair"
        label={translations.patientForms.drvMedicalForm.sectionFive.wheelchair}
        shouldStartWithNo
      />
      <YesNoRadioWithLabel
        elementName="orientation_disoriented_selected"
        label={translations.patientForms.drvMedicalForm.sectionFive.orientation}
        shouldStartWithNo
        sideMutation={(_value, mutate) => {
          mutate(null, "orientation_disoriented");
        }}
        subForms={{
          [PREDICAMENT_STATE_NO]: (
            <TextAreaWithLabel
              elementName="orientation_disoriented"
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .orientationDescription
              }
              marginOverride={margin(0, 0, 1)}
              withFlatModel
            />
          ),
        }}
      />
      <YesNoRadioWithLabel
        elementName="carelevel_selected"
        label={
          translations.patientForms.drvMedicalForm.sectionFive.carelevelLabel
        }
        shouldStartWithNo
        sideMutation={(_value, mutate) => {
          mutate(null, "carelevel");
        }}
        subForms={{
          [PREDICAMENT_STATE_YES]: (
            <SelectWithLabel
              elementName="carelevel"
              label={
                translations.patientForms.drvMedicalForm.sectionFive
                  .carelevelDescription
              }
              options={carelevelSelectOptions}
              withFlatModel
              wrapperSx={{ width: "100%" }}
            />
          ),
        }}
      />
      <ConnectedCheckboxGroup
        elementName="transport_type"
        formGroupStyle={{ gap: sizing(1) }}
        items={patientCapabilityTravelOptions}
        label={translations.patientForms.drvMedicalForm.sectionFive.travel}
      />
      <ConnectedCheckboxGroup
        elementName="outpatient_not_possible_reason"
        formGroupStyle={{ gap: sizing(1) }}
        items={outpatientReasonOptions}
        label={
          translations.patientForms.drvMedicalForm.sectionFive.medicalReason
        }
      />
    </Section>
  );
};
